import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ChartLineUp, CurrencyDollar, CursorClick, Receipt,
  CircleNotch, ArrowsClockwise, Check, X, Warning, Plug, PaperPlaneTilt,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified affiliate-network performance dashboard + sync configuration.
 * One screen replaces logging into Awin / CJ / Skimlinks / Commission
 * Factory dashboards individually.
 */
export default function AffiliateSyncTab() {
  const [tab, setTab] = useState('dashboard');  // dashboard | networks | runs

  return (
    <div className="space-y-6" data-testid="sync-tab">
      <div className="flex gap-1 border-b-2 border-[#0A0A0A]">
        {[
          { id: 'dashboard', label: 'Performance Dashboard', icon: ChartLineUp },
          { id: 'networks',  label: 'Network Connections', icon: Plug },
          { id: 'runs',      label: 'Sync Log', icon: Receipt },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wide border-b-4 -mb-[2px] transition-colors ${tab === t.id ? 'border-[#FF3B30] text-[#0A0A0A]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}
            data-testid={`sync-tab-${t.id}`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <Dashboard />}
      {tab === 'networks' && <NetworkConnections />}
      {tab === 'runs' && <SyncRuns />}
    </div>
  );
}


// ============== DASHBOARD ==============

function Dashboard() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [byNetwork, setByNetwork] = useState([]);
  const [byStore, setByStore] = useState([]);
  const [clicksByDay, setClicksByDay] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, n, st, c] = await Promise.all([
        axios.get(`${API}/admin/dashboard/summary`, { params: { days }, withCredentials: true }),
        axios.get(`${API}/admin/dashboard/commissions`, { params: { days, group_by: 'network' }, withCredentials: true }),
        axios.get(`${API}/admin/dashboard/commissions`, { params: { days, group_by: 'store_id' }, withCredentials: true }),
        axios.get(`${API}/admin/dashboard/clicks`, { params: { days, group_by: 'day' }, withCredentials: true }),
      ]);
      setSummary(s.data);
      setByNetwork(n.data);
      setByStore(st.data);
      setClicksByDay(c.data);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading || !summary) {
    return <div className="flex items-center gap-2 text-[#52525B]"><CircleNotch size={20} className="animate-spin"/> Loading dashboard…</div>;
  }

  const stats = [
    { label: 'Clicks', value: summary.clicks.toLocaleString(), icon: CursorClick, hint: 'Outbound retailer clicks' },
    { label: 'Transactions', value: summary.transactions.toLocaleString(), icon: Receipt, hint: 'Tracked conversions' },
    { label: 'Total Commission', value: '$' + summary.total_commission.toFixed(2), icon: CurrencyDollar, hint: 'All currencies summed (raw)' },
    { label: 'Approved', value: '$' + summary.approved_commission.toFixed(2), icon: Check, hint: 'Locked-in earnings', color: '#10B981' },
    { label: 'Pending', value: '$' + summary.pending_commission.toFixed(2), icon: CircleNotch, hint: 'Awaiting approval', color: '#F59E0B' },
    { label: 'Declined', value: '$' + summary.declined_commission.toFixed(2), icon: X, hint: 'Rejected by advertiser', color: '#FF3B30' },
    { label: 'CVR', value: summary.conversion_rate.toFixed(2) + '%', icon: ChartLineUp, hint: 'Conversion rate' },
    { label: 'EPC', value: '$' + summary.epc.toFixed(4), icon: ChartLineUp, hint: 'Earnings per click' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-lg font-black uppercase tracking-tight">Last {days} days</h3>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${days === d ? 'border-[#FF3B30] bg-[#FF3B30] text-white' : 'border-[#E4E4E7] text-[#52525B] hover:border-[#0A0A0A] hover:text-[#0A0A0A]'}`}
              data-testid={`range-${d}d`}
            >
              {d}D
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map(s => (
          <div key={s.label} className="border-2 border-[#0A0A0A] bg-white p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-2">
              <s.icon size={18} style={{ color: s.color || '#0A0A0A' }} />
              <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{s.label}</p>
            </div>
            <p className="text-2xl font-black tracking-tighter" style={{ color: s.color }}>{s.value}</p>
            <p className="text-[10px] uppercase tracking-wide text-[#A1A1AA] mt-1">{s.hint}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card title="By Network" data={byNetwork} testid="by-network" />
        <Card title="By Store" data={byStore} testid="by-store" />
      </div>

      <SparkChart title="Daily Clicks" data={clicksByDay} testid="clicks-chart" />
    </div>
  );
}


function Card({ title, data, testid }) {
  return (
    <div className="border-2 border-[#0A0A0A] bg-white p-5" data-testid={testid}>
      <h4 className="text-sm font-black uppercase tracking-tight mb-3">{title}</h4>
      {data.length === 0 ? (
        <p className="text-sm text-[#A1A1AA] italic">No transactions in this period.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-[#52525B] border-b border-[#E4E4E7]">
              <th className="text-left py-2 font-bold">Key</th>
              <th className="text-right py-2 font-bold">Commission</th>
              <th className="text-right py-2 font-bold">Sales</th>
              <th className="text-right py-2 font-bold">Txns</th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 10).map((r, i) => (
              <tr key={i} className="border-b border-[#E4E4E7] last:border-0">
                <td className="py-2 font-mono">{r.key}</td>
                <td className="py-2 text-right font-bold">${r.commission.toFixed(2)}</td>
                <td className="py-2 text-right text-[#52525B]">${r.sales.toFixed(2)}</td>
                <td className="py-2 text-right text-[#52525B]">{r.transactions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function SparkChart({ title, data, testid }) {
  if (!data || data.length === 0) {
    return (
      <div className="border-2 border-[#0A0A0A] bg-white p-5" data-testid={testid}>
        <h4 className="text-sm font-black uppercase tracking-tight mb-3">{title}</h4>
        <p className="text-sm text-[#A1A1AA] italic">No data yet.</p>
      </div>
    );
  }
  const max = Math.max(...data.map(d => d.clicks), 1);
  return (
    <div className="border-2 border-[#0A0A0A] bg-white p-5" data-testid={testid}>
      <h4 className="text-sm font-black uppercase tracking-tight mb-3">{title}</h4>
      <div className="flex items-end gap-1 h-32">
        {data.map((d, i) => (
          <div
            key={i}
            className="flex-1 bg-[#FF3B30] hover:bg-[#0A0A0A] transition-colors relative group"
            style={{ height: `${(d.clicks / max) * 100}%`, minHeight: '2px' }}
            title={`${d.key}: ${d.clicks} clicks`}
          />
        ))}
      </div>
      <p className="text-[10px] uppercase tracking-wide text-[#A1A1AA] mt-2">{data[0]?.key} → {data[data.length-1]?.key}</p>
    </div>
  );
}


// ============== NETWORK CONNECTIONS ==============

function NetworkConnections() {
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // network_id currently being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sync/connectors`, { withCredentials: true });
      setConns(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAll = async () => {
    if (!window.confirm('Run sync for all active, configured connectors now?')) return;
    await axios.post(`${API}/admin/sync/run-all`, {}, { withCredentials: true });
    load();
  };

  if (loading) return <div className="flex items-center gap-2"><CircleNotch size={20} className="animate-spin"/> Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#52525B]">{conns.filter(c => c.active && c.configured).length} active and configured · {conns.filter(c => c.status === 'stub').length} ready to enable when approved</p>
        <button
          onClick={runAll}
          className="inline-flex items-center gap-2 bg-[#FF3B30] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide hover:bg-[#D62828] transition-colors"
          data-testid="sync-run-all"
        >
          <ArrowsClockwise size={14}/> Run Sync Now
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {conns.map(c => (
          <NetworkCard
            key={c.network_id}
            connector={c}
            isEditing={editing === c.network_id}
            onEdit={() => setEditing(c.network_id)}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        ))}
      </div>
    </div>
  );
}


function NetworkCard({ connector, isEditing, onEdit, onCancel, onSaved }) {
  const [creds, setCreds] = useState({});
  const [active, setActive] = useState(connector.active);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (isEditing) {
      // Pre-fill with empty values; user types fresh secrets each time
      const init = {};
      connector.required_fields.forEach(f => { init[f] = ''; });
      setCreds(init);
    }
  }, [isEditing, connector.required_fields]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/sync/credentials/${connector.network_id}`,
        { credentials: creds, active }, { withCredentials: true });
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/sync/run/${connector.network_id}`,
        { lookback_days: 30 }, { withCredentials: true });
      alert(data.status === 'ok'
        ? `Sync OK — ${data.transactions_upserted} transactions`
        : `Sync FAILED: ${data.error}`);
      onSaved();
    } finally {
      setSyncing(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Remove ${connector.network_label} credentials?`)) return;
    await axios.delete(`${API}/admin/sync/credentials/${connector.network_id}`, { withCredentials: true });
    onSaved();
  };

  const isStub = connector.status === 'stub';
  const badge = isStub ? ['Coming soon', 'bg-[#FEF3C7] text-[#92400E]']
    : connector.active && connector.configured ? ['Active', 'bg-[#10B981] text-white']
    : connector.configured ? ['Configured', 'bg-[#E4E4E7] text-[#52525B]']
    : ['Not configured', 'bg-white border border-[#E4E4E7] text-[#52525B]'];

  return (
    <div className="border-2 border-[#0A0A0A] bg-white p-5" data-testid={`connector-${connector.network_id}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-base font-black uppercase tracking-tight">{connector.network_label}</h4>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 ${badge[1]}`}>
          {badge[0]}
        </span>
      </div>

      {connector.last_sync_at && (
        <p className="text-xs text-[#52525B] mb-2">
          Last sync: {new Date(connector.last_sync_at).toLocaleString()} · {connector.last_sync_status === 'ok'
            ? <span className="text-[#10B981] font-bold">{connector.last_sync_count} txns</span>
            : <span className="text-[#FF3B30] font-bold">error</span>}
        </p>
      )}
      {connector.last_sync_status === 'error' && connector.last_sync_error && (
        <p className="text-xs text-[#FF3B30] mb-2 font-mono">{connector.last_sync_error}</p>
      )}

      {isStub && !isEditing && (
        <p className="text-xs text-[#92400E] mb-3">
          Connector framework ready. Paste credentials to request activation —
          a backend implementation file will be added so sync starts running.
        </p>
      )}

      {isEditing ? (
        <div className="space-y-3">
          {connector.required_fields.map(field => (
            <div key={field}>
              <label className="block text-[10px] font-bold uppercase tracking-wide mb-1">
                {field.replace(/_/g, ' ')}
                {connector.field_help?.[field] && (
                  <span className="text-[#A1A1AA] font-normal normal-case ml-1">— {connector.field_help[field]}</span>
                )}
              </label>
              <input
                type={field.includes('secret') || field.includes('token') || field.includes('key') ? 'password' : 'text'}
                value={creds[field] || ''}
                onChange={(e) => setCreds({ ...creds, [field]: e.target.value })}
                className="w-full border-2 border-[#E4E4E7] px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]"
                data-testid={`field-${connector.network_id}-${field}`}
              />
            </div>
          ))}
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="w-4 h-4 accent-[#FF3B30]"
              data-testid={`active-${connector.network_id}`}
            />
            <span className="text-xs font-bold uppercase tracking-wide">Active (auto-sync daily)</span>
          </label>
          <div className="flex gap-2 pt-2">
            <button
              onClick={save}
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-1 bg-[#0A0A0A] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide hover:bg-[#FF3B30] disabled:opacity-50 transition-colors"
              data-testid={`save-${connector.network_id}`}
            >
              {saving ? <CircleNotch size={12} className="animate-spin"/> : <Check size={12}/>}
              Save
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors"
              data-testid={`cancel-${connector.network_id}`}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1 border-2 border-[#0A0A0A] px-3 py-1.5 text-xs font-bold uppercase tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
            data-testid={`edit-${connector.network_id}`}
          >
            {connector.configured ? 'Edit credentials' : 'Add credentials'}
          </button>
          {!isStub && connector.configured && (
            <button
              onClick={runSync}
              disabled={syncing}
              className="inline-flex items-center gap-1 bg-[#FF3B30] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide hover:bg-[#D62828] disabled:opacity-50 transition-colors"
              data-testid={`run-${connector.network_id}`}
            >
              {syncing ? <CircleNotch size={12} className="animate-spin"/> : <PaperPlaneTilt size={12}/>}
              Run sync
            </button>
          )}
          {connector.configured && (
            <button
              onClick={clear}
              className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[#FF3B30] hover:underline"
              data-testid={`clear-${connector.network_id}`}
            >
              Clear
            </button>
          )}
          {connector.homepage && (
            <a
              href={connector.homepage} target="_blank" rel="noopener noreferrer"
              className="ml-auto text-[10px] font-bold uppercase tracking-wide text-[#A1A1AA] hover:text-[#FF3B30]"
            >
              Get keys ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}


// ============== SYNC RUNS ==============

function SyncRuns() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/sync/runs`, { params: { limit: 50 }, withCredentials: true })
      .then(({ data }) => setRuns(data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2"><CircleNotch size={20} className="animate-spin"/> Loading…</div>;

  return (
    <div className="border-2 border-[#0A0A0A] bg-white" data-testid="sync-runs">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#0A0A0A] text-white text-[10px] uppercase tracking-wide">
            <th className="text-left p-3 font-bold">Started</th>
            <th className="text-left p-3 font-bold">Network</th>
            <th className="text-left p-3 font-bold">Status</th>
            <th className="text-right p-3 font-bold">Fetched</th>
            <th className="text-right p-3 font-bold">Upserted</th>
            <th className="text-left p-3 font-bold">Error</th>
          </tr>
        </thead>
        <tbody>
          {runs.length === 0 && (
            <tr><td colSpan={6} className="p-6 text-center text-[#A1A1AA] italic">No sync runs yet. Run one from the Networks tab.</td></tr>
          )}
          {runs.map((r, i) => (
            <tr key={i} className="border-b border-[#E4E4E7] last:border-0">
              <td className="p-3 text-xs text-[#52525B]">{new Date(r.started_at).toLocaleString()}</td>
              <td className="p-3 font-mono font-bold">{r.network}</td>
              <td className="p-3">
                {r.status === 'ok' ? (
                  <span className="inline-flex items-center gap-1 text-[#10B981] font-bold text-xs"><Check size={12}/> OK</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[#FF3B30] font-bold text-xs"><Warning size={12}/> Error</span>
                )}
              </td>
              <td className="p-3 text-right text-xs">{r.transactions_fetched ?? 0}</td>
              <td className="p-3 text-right text-xs">{r.transactions_upserted ?? 0}</td>
              <td className="p-3 text-xs text-[#FF3B30] font-mono max-w-xs truncate" title={r.error || ''}>{r.error || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
